import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Layers } from 'lucide-react';
import { useMasterLabel } from '../../hooks/useMasterLabel';
import CommonLoader from '../CommonLoader';
import { doctorCurrencySymbol } from '../../utils/currency';

// Per-row translated specialty label. useMasterLabel is a hook, so it must be
// called once per child component instance — never inside a .map callback.
const SpecialtyName = ({ specialty }) => {
  const label = useMasterLabel('masterSpecialty', specialty?.specialtyId, 'name', specialty?.specialtyName || '');
  return (
    <span data-testid="specialty-type-name" className="font-bold text-gray-900 capitalize">
      {label}
    </span>
  );
};

const SpecialtyTypeBreakdown = ({ specialtyTypeBreakdown, loading }) => {
  const { t } = useTranslation('dashboard');

  const { selectedDoctor } = useSelector((state) => state.doctor);

  // The doctor's currency, from their country's currency code (utils/currency.js).
  const getCurrencySymbol = () => doctorCurrencySymbol(selectedDoctor);

  // Local toggle: which metric the per-type bars represent.
  const [metric, setMetric] = useState('appointments');

  const cur = getCurrencySymbol();
  const specialties = specialtyTypeBreakdown?.data || [];
  const hasData = specialties.length > 0;

  return (
    <div
      data-testid="specialty-type-breakdown"
      className="bg-white rounded-xl p-4 border border-gray-200/50 shadow-md"
    >
      <div
        data-testid="specialty-type-breakdown-header"
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4"
      >
        <h3
          data-testid="specialty-type-breakdown-title"
          className="text-base sm:text-lg font-bold text-gray-900 flex items-center"
        >
          <Layers className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#4ABDBD]" />
          {t('dashboard.detail_by_specialty', 'Detail by specialty & appointment type')}
        </h3>

        {/* Segmented toggle: appointments vs revenue */}
        <div
          data-testid="specialty-type-breakdown-toggle"
          className="inline-flex self-start sm:self-auto rounded-lg border border-gray-200 p-0.5 bg-gray-50"
        >
          <button
            type="button"
            data-testid="specialty-type-breakdown-toggle-appointments"
            onClick={() => setMetric('appointments')}
            className={`px-3 py-1 text-xs sm:text-sm font-medium rounded-md transition-colors ${
              metric === 'appointments'
                ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('dashboard.appointments_label', 'Appointments')}
          </button>
          <button
            type="button"
            data-testid="specialty-type-breakdown-toggle-revenue"
            onClick={() => setMetric('revenue')}
            className={`px-3 py-1 text-xs sm:text-sm font-medium rounded-md transition-colors ${
              metric === 'revenue'
                ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('dashboard.revenue', 'Revenue')}
          </button>
        </div>
      </div>

      {loading ? (
        <div
          data-testid="specialty-type-breakdown-loading"
          className="flex flex-col items-center justify-center py-12"
        >
          <CommonLoader />
        </div>
      ) : !hasData ? (
        <div
          data-testid="specialty-type-breakdown-empty"
          className="flex flex-col items-center justify-center py-12"
        >
          <Layers className="w-10 h-10 text-gray-300 mb-2" />
          <p data-testid="specialty-type-breakdown-empty-text" className="text-sm text-gray-600">
            {t('dashboard.no_data_for_period', 'No data for this period')}
          </p>
        </div>
      ) : (
        <div
          data-testid="specialty-type-breakdown-grid"
          className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start"
        >
          {specialties.map((spec, specIndex) => {
            const colour = spec?.colour || '#4ABDBD';
            const types = spec?.types || [];
            const rowKey = spec?.specialtyId ?? spec?.specialtyName ?? specIndex;
            return (
              <div
                key={rowKey}
                data-testid={`specialty-type-card-${rowKey}`}
                className="rounded-xl border border-gray-200/60 p-4"
              >
                {/* Header row */}
                <div
                  data-testid={`specialty-type-card-header-${rowKey}`}
                  className="flex items-center justify-between gap-2 mb-2"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      data-testid={`specialty-type-card-dot-${rowKey}`}
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: colour }}
                    />
                    <SpecialtyName specialty={spec} />
                  </div>
                  <span
                    data-testid={`specialty-type-card-appts-${rowKey}`}
                    className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap shrink-0"
                  >
                    {(spec?.appts || 0).toLocaleString()} {t('dashboard.appts', 'appts')} · {spec?.apptShare || 0}%
                  </span>
                </div>

                {/* Meta line — each metric is a non-wrapping label+value pair so nothing
                    gets orphaned onto its own line; the row wraps as a whole with gap-y. */}
                <div
                  data-testid={`specialty-type-card-meta-${rowKey}`}
                  className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-xs"
                >
                  <span data-testid={`specialty-type-card-meta-revenue-${rowKey}`} className="whitespace-nowrap">
                    <span className="text-gray-400">{t('dashboard.rev', 'Rev')}</span>{' '}
                    <span className="font-semibold text-gray-700">{cur}{(spec?.revenue || 0).toLocaleString()}</span>
                  </span>
                  <span data-testid={`specialty-type-card-meta-ticket-${rowKey}`} className="whitespace-nowrap">
                    <span className="text-gray-400">{t('dashboard.ticket', 'Ticket')}</span>{' '}
                    <span className="font-semibold text-gray-700">{cur}{(spec?.ticket || 0).toLocaleString()}</span>
                  </span>
                  <span data-testid={`specialty-type-card-meta-noshow-${rowKey}`} className="whitespace-nowrap">
                    <span className="text-gray-400">{t('dashboard.no_show', 'No-show')}</span>{' '}
                    <span className="font-semibold text-gray-700">{spec?.noShowPercent || 0}%</span>
                  </span>
                  <span data-testid={`specialty-type-card-meta-hourly-${rowKey}`} className="whitespace-nowrap">
                    <span className="font-semibold text-gray-700">{cur}{(spec?.hourlyRate || 0).toLocaleString()}</span>
                    <span className="text-gray-400">/h</span>
                  </span>
                </div>

                {/* Type rows */}
                <div data-testid={`specialty-type-card-types-${rowKey}`} className="space-y-2.5">
                  {types.length > 0 ? (
                    types.map((type, typeIndex) => {
                      const percent =
                        metric === 'revenue' ? type?.revenuePercent || 0 : type?.countPercent || 0;
                      const rightValue =
                        metric === 'revenue'
                          ? `${cur}${(type?.revenue || 0).toLocaleString()}`
                          : (type?.count || 0).toLocaleString();
                      const typeKey = type?.name ?? typeIndex;
                      return (
                        <div
                          key={typeKey}
                          data-testid={`specialty-type-row-${rowKey}-${typeKey}`}
                          className="space-y-1"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span
                              data-testid={`specialty-type-row-name-${rowKey}-${typeKey}`}
                              className="text-xs sm:text-sm text-gray-700 truncate"
                            >
                              {type?.name || '-'}
                            </span>
                            <span
                              data-testid={`specialty-type-row-value-${rowKey}-${typeKey}`}
                              className="text-xs sm:text-sm font-semibold text-gray-900 whitespace-nowrap shrink-0"
                            >
                              {rightValue}{' '}
                              <span className="text-gray-400 font-normal">({percent}%)</span>
                            </span>
                          </div>
                          <div
                            data-testid={`specialty-type-row-track-${rowKey}-${typeKey}`}
                            className="bg-gray-100 rounded h-2 overflow-hidden"
                          >
                            <div
                              data-testid={`specialty-type-row-fill-${rowKey}-${typeKey}`}
                              className="h-2 rounded"
                              style={{ width: `${Math.min(Math.max(Number(percent) || 0, 0), 100)}%`, backgroundColor: colour }}
                            />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p
                      data-testid={`specialty-type-card-no-types-${rowKey}`}
                      className="text-xs text-gray-400"
                    >
                      {t('dashboard.no_appointment_types', 'No appointment types')}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SpecialtyTypeBreakdown;
